import { CategoryType } from "../rules/rulesAPI";
import { type GetAsfTransactionsResponse } from "@qivia/schemas-asf";
import { merchantGroups, type MerchantGroup } from "@qivia/schemas-shared";
import {
  type GetRejectedAuthorizationsOfficeResponseItem,
  type ReasonsRefused,
} from "@qivia/schemas-transaction-auth";
import type { CreateMerchantCodePostPayload } from "@qivia/schemas-office";

export const MerchantGroupsList = merchantGroups;
export type MerchantGroups = MerchantGroup;

export type LinkedTransaction = {
  uuid: string;
  executionDate: string;
  amount: number;
};
export type TransactionType = "EXPENSE" | "REFUND";
export type TransactionStatus =
  | "ACCEPTED"
  | "REJECTED"
  | "SUSPICIOUS"
  | "PENDING_ADJUSTEMENT"
  | "PENDING_CAPTURE"
  | "PENDING_CONFIRMATION"
  | "CANCELED";

export type PublicTransactionStatus =
  | "ACCEPTED"
  | "REJECTED"
  | "SUSPICIOUS"
  | "PENDING"
  | "CANCELED"
  | "REFUND";

export type SupportingDocumentStatus = "ACCEPTED" | "MISSING" | "";

export type EmspAuthorizationStatus =
  | "ALLOWED"
  | "NOT_ALLOWED"
  | "NO_CREDIT"
  | "EXPIRED"
  | "BLOCKED";

export type EmspLocationType =
  | "ON_STREET"
  | "PARKING_GARAGE"
  | "UNDERGROUND_GARAGE"
  | "PARKING_LOT"
  | "OTHER"
  | "UNKNOWN";

export type PublicTransactionType = {
  uuid: string;
  amount: number;
  executionDate: string;
  registrationNumber: string;
  merchantName: string;
  merchantCity: string;
  merchantPostalCode: string | null;
  mileage: number | null;
  quantity: number;
  consumption: string;
  cardLast4Digits: string;
  driverName: string;
  driverPhone: string;
  supportingDocumentUuid?: string;
  status: TransactionStatus;
  vehicleReference: string;
  driverLastName: string;
  driver: string;
  ruleName: string;
  rule: string;
  vehicleUuid: string;
  card: string;
  category: CategoryType;
  fuel: string;
  carbonEmission: number;
  hasToRetrieveSupportingDocuments: boolean;
  merchantGroup: MerchantGroups | null;
  vehicleAnalyticCode: string | null;
  type: TransactionType;
  refundTransaction: LinkedTransaction | null;
  originalTransaction: LinkedTransaction | null;
};

export type PublicAuthorizationType =
  GetRejectedAuthorizationsOfficeResponseItem;

export type PublicTransactionDisplayed = {
  date: string;
  time: string;
  amount: number | null;
  merchantName: string;
  merchantGroup: MerchantGroups | null;
  category: CategoryType | null;
  driverName: string | null;
  status: PublicTransactionStatus;
  supportingDocumentStatus: SupportingDocumentStatus;
  mileage: number | null;
  fuel: string | null;
  quantity: number | null;
  vehicleReference: string | null;
};

export type BadgeTransactionDisplayedApi = GetAsfTransactionsResponse[number];

export type PublicBadgeTransaction = {
  uuid: string;
  date: string;
  badgeNumber: string;
  category: "TOLL" | "PARKING";
  amount: number | null;
  vehicleInfo: string;
  stationsInfo: string;
  type: TransactionType;
  entranceStationName: string;
  exitStationName: string;
};

export type PublicBadgeTransactionDisplayed = {
  date: string;
  badgeNumber: string;
  category: "TOLL" | "PARKING";
  amount: number | null;
  vehicleInfo: string;
  stationsInfo: string;
};

export type PublicEmspCdrsDisplayed = {
  startAt: string;
  stopAt: string;
  totalCost: number;
  totalEnergy: number;
  totalTime: number;
  totalParkingTime: number;
  chargingTime: number | null;
  authorizationStatus: EmspAuthorizationStatus;
  locationCity: string;
  registrationNumber: string;
  identifier: string;
  vehicleReference: string;
  driverName: string | null;
  departmentName: string | null;
};

export type PublicEmspCdrs = PublicEmspCdrsDisplayed & {
  uuid: string;
  cardUuid: string;
  vehicleUuid: string | null;
  driverUuid: string | null;
  currency: string;
  locationName: string;
  locationType: string;
  locationCountry: string;
  locationPostalCode: string;
  locationAddress: string;
  last4Digits: string;
};

export type CreateMerchantCode = CreateMerchantCodePostPayload & {
  companyUuid: string;
};

export type PublicTransactionsPropsType = {
  uuid: string;
  amount: number | null;
  registrationNumber: string | null;
  merchantName: string;
  merchantCity: string;
  cardLast4Digits: string;
  driverName: string | null;
  driverPhone?: string;
  status: PublicTransactionStatus;
  date: string;
  time: string;
  reasonsRefused?: ReasonsRefusedEnum[] | null;
  mileage: number | null;
  consumption?: string;
  supportingDocumentUuid?: string;
  vehicleReference: string | null;
  supportingDocumentStatus: SupportingDocumentStatus;
  driverLastName: string | null;
  driver: string | null;
  ruleName: string;
  ruleUuid: string;
  vehicleUuid: string;
  cardUuid: string;
  category: CategoryType | null;
  fuel: string | null;
  quantity: number | null;
  carbonEmission?: number;
  areSupportingDocumentsMandatory?: boolean;
  merchantGroup: MerchantGroups | null;
  categoryAlreadyCorrected: boolean | null;
  type: TransactionType;
  refundTransaction: LinkedTransaction | null;
  originalTransaction: LinkedTransaction | null;
};

export type GetSupportingDocumentType = {
  presignedUrl: string;
  fileName: string;
};

import { type SendSmsReminderPayload } from "@qivia/schemas-reminders";

export type SendSmsPayload = SendSmsReminderPayload;

export type TotalAmountPerCategoryType = {
  category: CategoryType;
  amount: number;
};

export type TotalAmountPerMerchantGroupType = {
  merchantGroup: MerchantGroups | null;
  amount: number;
};

export type DateValueType = {
  date: string;
  value: number;
};

export type AveragePricePerLiterPerFuelType = {
  fuel: string;
  data: DateValueType[];
};

export type GetTransactionsAmountsPerPeriodType = {
  year: {
    categoriesAmounts: TotalAmountPerCategoryType[];
    averagePricePerLiter: DateValueType[];
    totalCarbonEmission: DateValueType[];
    merchantGroupsAmounts: TotalAmountPerMerchantGroupType[];
    averagePricePerLiterPerFuel: AveragePricePerLiterPerFuelType[];
  };
  month: {
    categoriesAmounts: TotalAmountPerCategoryType[];
    averagePricePerLiter: DateValueType[];
    totalCarbonEmission: DateValueType[];
    merchantGroupsAmounts: TotalAmountPerMerchantGroupType[];
    averagePricePerLiterPerFuel: AveragePricePerLiterPerFuelType[];
  };
  quarter: {
    categoriesAmounts: TotalAmountPerCategoryType[];
    averagePricePerLiter: DateValueType[];
    totalCarbonEmission: DateValueType[];
    merchantGroupsAmounts: TotalAmountPerMerchantGroupType[];
    averagePricePerLiterPerFuel: AveragePricePerLiterPerFuelType[];
  };
};

export type ReasonsRefusedEnum = ReasonsRefused;

export type PayloadForGetTransactionsByCompanyByPeriod = {
  companyUuid: string;
  from?: string;
  to?: string;
};

export type PayloadForGetTransactionsOfADepartmentByPeriod = {
  departmentUuid: string;
  from?: string;
  to?: string;
};

export type TransactionByPeriodType = {
  uuid: string;
  date: string;
  driver: string | null;
};

export type PayloadForGetAuthorizationsRejectedOfADepartmentByPeriod = {
  departmentUuid: string;
  from?: string;
  to?: string;
};

export type PayloadForGetAuthorizationsRejectedByCompanyByPeriod = {
  companyUuid: string;
  from?: string;
  to?: string;
};

export type UploadSupportingDocumentResponse = {
  supportingDocumentId: string;
  fileName: string;
};

export type PayloadForUpdateTransactionDetails = {
  transactionUuid: string;
  mileage: number;
  updatedBy: string;
};
